import { useState, FormEvent } from 'react';

export default function NewsletterSignup() {
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);

      const response = await fetch('https://readdy.ai/api/form/d6gro42pd4a8ta1lecm0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Welcome to our community! You\'ll be notified about new events 🌸');
        setFormData({ name: '', email: '' });
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Unable to subscribe. Please check your connection.');
    }
  };

  return (
    <section className="py-20 px-6 bg-gradient-to-br from-amber-50 via-white to-green-50">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl p-12 relative overflow-hidden">
          {/* Decorative background pattern */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-amber-100 to-green-100 rounded-full blur-3xl opacity-30 -mr-32 -mt-32"></div>
          
          <div className="relative z-10">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-amber-700 rounded-2xl flex items-center justify-center shadow-lg">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
            </div>

            <h2 className="text-4xl font-bold text-center text-gray-900 mb-4">
              Stay Connected
            </h2>
            <p className="text-lg text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              Be the first to know about new events & community gatherings
            </p>

            <form 
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit} 
              className="max-w-xl mx-auto"
            >
              <div className="space-y-4 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white font-semibold py-4 px-8 rounded-xl hover:from-amber-700 hover:to-amber-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {status === 'loading' ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Subscribing...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-send-plane-fill mr-2"></i>
                    Subscribe to Updates
                  </span>
                )}
              </button>

              {status === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start">
                  <i className="ri-checkbox-circle-fill text-green-500 text-xl mr-3 mt-0.5"></i>
                  <p className="text-green-800 text-sm">{message}</p>
                </div>
              )}

              {status === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start">
                  <i className="ri-error-warning-fill text-red-500 text-xl mr-3 mt-0.5"></i>
                  <p className="text-red-800 text-sm">{message}</p>
                </div>
              )}
            </form>

            <p className="text-center text-sm text-gray-500 mt-6">
              We respect your privacy. Unsubscribe anytime.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
